import React from 'react';
import {
  Container,
  Typography,
  Paper,
  Divider,
  styled,
  alpha,
} from '@mui/material';

const MailTo = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecorationColor: alpha(theme.palette.primary.main, 0.4),

  ':hover': {
    textDecorationColor: theme.palette.primary.main,
  },
}));

const Policy = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          KIP Audio, LLC PRIVACY POLICY
        </Typography>

        <Typography variant="body1" paragraph>
          KIP Audio, LLC (the &ldquo;Company&rdquo;) is committed to maintaining
          robust privacy protections for its users. Our Privacy Policy
          (&ldquo;Privacy Policy&rdquo;) is designed to help you understand how
          we collect, use and safeguard the information you provide to us and to
          assist you in making informed decisions when using our Service.
        </Typography>

        <Typography variant="body1" paragraph>
          For purposes of this Agreement:
        </Typography>
        <ul style={{ paddingLeft: '2rem', marginBottom: '1.5rem' }}>
          <li>
            <Typography variant="body1" paragraph>
              &ldquo;Site&rdquo; refers to the Company&apos;s website, which can
              be accessed at https://kip.audio or through our mobile
              application.
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              &ldquo;Service&rdquo; refers to the Company&apos;s services
              accessed via the Site, in which users can stream and listen to
              music.
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              The terms &ldquo;we,&rdquo; &ldquo;us,&rdquo; and
              &ldquo;our&rdquo; refer to the Company.
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              &ldquo;You&rdquo; refers to you, as a user of our Site or our
              Service.
            </Typography>
          </li>
        </ul>

        <Typography variant="body1" paragraph>
          By accessing our Site or our Service, you accept our Privacy Policy
          and Terms of Use (found here: https://kip.audio/terms), and you
          consent to our collection, storage, use and disclosure of your
          Personal Information as described in this Privacy Policy.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h5" component="h2" gutterBottom>
          I. INFORMATION WE COLLECT
        </Typography>
        <Typography variant="body1" paragraph>
          We collect &ldquo;Non-Personal Information&rdquo; and &ldquo;Personal
          Information.&rdquo; Non-Personal Information includes information that
          cannot be used to personally identify you, such as anonymous usage
          data, general demographic information we may collect, referring/exit
          pages and URLs, platform types, preferences you submit and preferences
          that are generated based on the data you submit and number of clicks.
          Personal Information includes your email and first name and last name,
          which you submit to us through the registration process at the Site.
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          1. Information collected via Technology
        </Typography>
        <Typography variant="body1" paragraph>
          To activate the Service you do not need to submit any Personal
          Information other than your email address. To use the Service
          thereafter, you do not need to submit further Personal Information.
          However, in an effort to improve the quality of the Service, we track
          information provided to us by your browser or by our software
          application when you view or use the Service, such as the website you
          came from (known as the &ldquo;referring URL&rdquo;), the type of
          browser you use, the device from which you connected to the Service,
          the time and date of access, and other information that does not
          personally identify you.
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          2. Information you provide us by registering for an account
        </Typography>
        <Typography variant="body1" paragraph>
          In addition to the information provided automatically by your browser
          when you visit the Site, to become a subscriber to the Service you
          will need to create a personal profile. You can create a profile by
          registering with the Service and entering your email address, and
          creating a user name and a password. By registering, you are
          authorizing us to collect, store and use your email address in
          accordance with this Privacy Policy.
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          3. Children&apos;s Privacy
        </Typography>
        <Typography variant="body1" paragraph>
          The Site and the Service are not directed to anyone under the age of
          13. The Site does not knowingly collect or solicit information from
          anyone under the age of 13, or allow anyone under the age of 13 to
          sign up for the Service. In the event that we learn that we have
          gathered personal information from anyone under the age of 13 without
          the consent of a parent or guardian, we will delete that information
          as soon as possible. If you believe we have collected such
          information, please contact us at kip@kip.audio.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h5" component="h2" gutterBottom>
          II. HOW WE USE AND SHARE INFORMATION
        </Typography>
        <Typography variant="h6" component="h3" gutterBottom>
          Personal Information:
        </Typography>
        <Typography variant="body1" paragraph>
          Except as otherwise stated in this Privacy Policy, we do not sell,
          trade, rent or otherwise share for marketing purposes your Personal
          Information with third parties without your consent. We do share
          Personal Information with vendors who are performing services for the
          Company, such as the servers for our email communications who are
          provided access to user&apos;s email address for purposes of sending
          emails from us. Those vendors use your Personal Information only at
          our direction and in accordance with our Privacy Policy.
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Non-Personal Information
        </Typography>
        <Typography variant="body1" paragraph>
          In general, we use Non-Personal Information to help us improve the
          Service and customize the user experience. We also aggregate
          Non-Personal Information in order to track trends and analyze use
          patterns on the Site. This Privacy Policy does not limit in any way
          our use or disclosure of Non-Personal Information and we reserve the
          right to use and disclose such Non-Personal Information to our
          partners, advertisers and other third parties at our discretion.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h5" component="h2" gutterBottom>
          III. HOW WE PROTECT INFORMATION
        </Typography>
        <Typography variant="body1" paragraph>
          We implement security measures designed to protect your information
          from unauthorized access. Your account is protected by your account
          password and we urge you to take steps to keep your personal
          information safe by not disclosing your password and by logging out of
          your account after each use. We further protect your information from
          potential security breaches by implementing certain technological
          security measures including encryption, firewalls and secure socket
          layer technology. However, these measures do not guarantee that your
          information will not be accessed, disclosed, altered or destroyed by
          breach of such firewalls and secure server software. By using our
          Service, you acknowledge that you understand and agree to assume these
          risks.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h5" component="h2" gutterBottom>
          IV. YOUR RIGHTS REGARDING THE USE OF YOUR PERSONAL INFORMATION
        </Typography>
        <Typography variant="body1" paragraph>
          You have the right at any time to prevent us from contacting you for
          marketing purposes. When we send a promotional communication to a
          user, the user can opt out of further promotional communications by
          following the unsubscribe instructions provided in each promotional
          e-mail. You can also indicate that you do not wish to receive
          marketing communications from us in the &ldquo;Settings&rdquo; section
          of the Site. Please note that notwithstanding the promotional
          preferences you indicate by either unsubscribing or opting out in the
          &ldquo;Settings&rdquo; section of the Site, we may continue to send
          you administrative emails including, for example, periodic updates to
          our Privacy Policy.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h5" component="h2" gutterBottom>
          V. LINKS TO OTHER WEBSITES
        </Typography>
        <Typography variant="body1" paragraph>
          As part of the Service, we may provide links to or compatibility with
          other websites or applications. However, we are not responsible for
          the privacy practices employed by those websites or the information or
          content they contain. This Privacy Policy applies solely to
          information collected by us through the Site and the Service.
          Therefore, this Privacy Policy does not apply to your use of a third
          party website accessed by selecting a link on our Site or via our
          Service. To the extent that you access or use the Service through or
          on another website or application, then the privacy policy of that
          other website or application will apply to your access or use of that
          site or application. We encourage our users to read the privacy
          statements of other websites before proceeding to use them.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h5" component="h2" gutterBottom>
          VI. CHANGES TO OUR PRIVACY POLICY
        </Typography>
        <Typography variant="body1" paragraph>
          The Company reserves the right to change this policy and our Terms of
          Service at any time. We will notify you of significant changes to our
          Privacy Policy by sending a notice to the primary email address
          specified in your account or by placing a prominent notice on our
          site. Significant changes will go into effect 30 days following such
          notification. Non-material changes or clarifications will take effect
          immediately. You should periodically check the Site and this privacy
          page for updates.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h5" component="h2" gutterBottom>
          VII. CONTACT US
        </Typography>
        <Typography variant="body1" paragraph>
          If you have any questions regarding this Privacy Policy or the
          practices of this Site, please contact us by sending an email to{' '}
          <MailTo href="mailto:kip@kip.audio">kip@kip.audio</MailTo>
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
          Last Updated: This Privacy Policy was last updated on 2025-06-05.
        </Typography>
      </Paper>
    </Container>
  );
};

export default Policy;
